import {
  MENUITEMSFORADMIN,
  MENUITEMSFORACCOUNTS,
  MENUITEMSFOREMPLOYEE,
  MENUITEMSFOROFFICER,
} from "../constants/menu";

// The sidebar has always been this app's whole authorisation model: four
// hand-written menu arrays, one per `currentAdmin.status`, and nothing else
// checking the role. This turns that same data into an answer to "may this
// admin open this URL?", so the dashboard can lock tiles a role cannot use
// and allRoutes can turn away a URL typed straight into the address bar.
// Deriving it from the menus rather than from a second hand-maintained list
// means a menu edit stays the single place a role's access changes.
const MENUS_BY_ROLE = {
  Admin: MENUITEMSFORADMIN,
  Accounts: MENUITEMSFORACCOUNTS,
  Employee: MENUITEMSFOREMPLOYEE,
  Officer: MENUITEMSFOROFFICER,
};

const collectPaths = (items, acc) => {
  (items || []).forEach((item) => {
    if (item.path) acc.push(item.path);
    if (item.children) collectPaths(item.children, acc);
  });
  return acc;
};

// The menu arrays are module constants, so each role's path list only needs
// building once.
const pathCache = {};
const menuPathsFor = (role) => {
  if (!pathCache[role]) {
    pathCache[role] = collectPaths(MENUS_BY_ROLE[role], []);
  }
  return pathCache[role];
};

// Segment-aligned, so "/lot" does not match "/lot-transport".
const under = (path, prefix) =>
  path === prefix || path.startsWith(prefix + "/");

// An exact allowlist of menu paths does not work: roughly 55 legitimate
// detail routes (/expense/loans/:month, /cash-summary-by-day/:date,
// /business-consultancy/sourcing2/:invoiceId ...) appear in no menu at all
// and would lock out every role including Admin. So routes are grouped into
// sections instead — `routes` are the URL prefixes a section owns, and
// `grantedBy` are the menu-path prefixes that unlock it. The longest
// matching `routes` entry wins, which is what keeps /expense/approve-expense
// Admin-only while the rest of /expense opens up for Accounts.
const SECTIONS = [
  { routes: ["/settings"], grantedBy: ["/settings"] },
  { routes: ["/attendance"], grantedBy: ["/attendance"] },
  { routes: ["/admins"], grantedBy: ["/admins"] },
  { routes: ["/users"], grantedBy: ["/users"] },
  {
    routes: ["/communication", "/communications"],
    grantedBy: ["/communication"],
  },
  {
    routes: ["/expense/approve-expense"],
    grantedBy: ["/expense/approve-expense"],
  },
  { routes: ["/expense", "/expenses"], grantedBy: ["/expense"] },
  {
    routes: ["/cash-summary", "/cash-summary-by-day"],
    grantedBy: ["/cash-summary"],
  },
  { routes: ["/p2p/payment-request"], grantedBy: ["/p2p/payment-request"] },
  { routes: ["/p2p"], grantedBy: ["/p2p"] },
  { routes: ["/business-consultancy"], grantedBy: ["/business-consultancy"] },
  { routes: ["/hand-carry"], grantedBy: ["/hand-carry"] },
  {
    routes: ["/booking-request", "/booking-request-warehouse"],
    grantedBy: ["/booking-request"],
  },
  { routes: ["/lot"], grantedBy: ["/lot"] },
  { routes: ["/order", "/orders"], grantedBy: ["/order", "/orders"] },
  { routes: ["/delivery", "/delivery-express"], grantedBy: ["/delivery"] },
  { routes: ["/invoice-express"], grantedBy: ["/invoice-express"] },
  { routes: ["/invoice-d2d-freight"], grantedBy: ["/invoice-d2d-freight"] },
  {
    routes: ["/invoice-by-orderId", "/invoice-by-bookingId"],
    grantedBy: ["/invoice-d2d-freight", "/invoice-express"],
  },
  { routes: ["/pay-invoice-express"], grantedBy: ["/pay-invoice-express"] },
  { routes: ["/pay-invoice"], grantedBy: ["/pay-invoice"] },
  { routes: ["/payments-express"], grantedBy: ["/payments-express"] },
  { routes: ["/payments"], grantedBy: ["/payments"] },
  {
    routes: ["/payment-request-express"],
    grantedBy: ["/payment-request-express"],
  },
  { routes: ["/payment-request"], grantedBy: ["/payment-request"] },
  { routes: ["/refund"], grantedBy: ["/refund"] },
  {
    routes: ["/calculation", "/calcualation", "/expressOrder"],
    grantedBy: ["/calculation"],
  },
  { routes: ["/express-rates"], grantedBy: ["/express-rates"] },
  { routes: ["/d2d-rates"], grantedBy: ["/d2d-rates"] },
];

// Open to every signed-in admin whatever their role: the login/landing route,
// the page everyone is redirected to, and the tracking lookup behind the
// header search box, which header.js renders for all four roles.
const ALWAYS_ALLOWED = ["/", "/dashboard", "/searched-order"];

const sectionFor = (path) => {
  let best = null;
  let bestLength = -1;
  SECTIONS.forEach((section) => {
    section.routes.forEach((route) => {
      if (under(path, route) && route.length > bestLength) {
        best = section;
        bestLength = route.length;
      }
    });
  });
  return best;
};

// CRA serves this app from the root today, so PUBLIC_URL is "" and pathname
// needs no trimming — but every route in allRoutes.js is built with the
// prefix, so honour it rather than depending on it staying empty.
export const stripPublicUrl = (pathname) => {
  const base = process.env.PUBLIC_URL || "";
  if (base && pathname.startsWith(base)) {
    return pathname.slice(base.length) || "/";
  }
  return pathname;
};

export const canAccessPath = (currentAdmin, pathname) => {
  const path = stripPublicUrl(pathname || "/");
  if (ALWAYS_ALLOWED.some((allowed) => under(path, allowed))) return true;

  const section = sectionFor(path);
  // Nothing claims this path. Rather than making routes disappear the moment
  // somebody forgets to register a section, unclaimed paths stay open — this
  // is what keeps the orphans nothing links to (/recharge/*, /media,
  // /menus/*, /pages/*, /data-table) behaving exactly as they do today. A new
  // area of the app earns a section above.
  if (!section) return true;

  const role = currentAdmin && currentAdmin.status;
  // An unrecognised status gets nothing beyond ALWAYS_ALLOWED, which is
  // already what such an admin sees in the sidebar: sidebar.js picks a menu
  // with four `if`s and no `else`, so they get a blank one.
  if (!MENUS_BY_ROLE[role]) return false;

  const menuPaths = menuPathsFor(role);
  return section.grantedBy.some((grant) =>
    menuPaths.some((menuPath) => under(menuPath, grant)),
  );
};

export default canAccessPath;
